import React from 'react';
import { Icon } from '../Icon';
import { Upload } from '../../redux/reducers/uploadReducer';

interface UploadItemProps {
  item: Upload;
  onRemove: (id: string) => void;
  onCancel?: (id: string) => void;
}

const UploadItemComponentInner: React.FC<UploadItemProps> = ({
  item,
  onRemove,
  onCancel,
}) => {
  const isCompleted = item.status === 'completed';
  const isFailed = item.status === 'failed';
  const isUploading = item.status === 'uploading';

  return (
    <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow animate-fade-in">
      <div className="flex-shrink-0">
        {isUploading && (
          <div className="w-4 h-4 rounded-full border-2 border-gray-200 dark:border-gray-600 border-t-primary-500 animate-spin"></div>
        )}
        {isCompleted && (
          <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
            <Icon name="tick" className="w-3 h-3" />
          </div>
        )}
        {isFailed && (
          <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
            <Icon name="exclamation" className="w-3 h-3 text-red-600" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-primary-900 dark:text-gray-100 truncate">
          {item.fileName} ({isUploading && item.progress + '%'})
        </p>
        {isUploading && (
          <div className="mt-1.5 w-full h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-[width] duration-300 ease-out"
              style={{ width: `${item.progress}%` }}
            />
          </div>
        )}
        {isFailed && (
          <p className="text-xs text-red-600 mt-1">
            {item.error || 'Upload failed'}
          </p>
        )}
        {isCompleted && (
          <p className="text-xs text-green-600 mt-1">Completed</p>
        )}
      </div>

      {isUploading && onCancel && (
        <button
          onClick={() => onCancel(item.id)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 mr-2"
          title="Cancel upload"
        >
          <Icon name="close" className="w-4 h-4" />
        </button>
      )}

      {(isCompleted || isFailed) && (
        <button
          onClick={() => onRemove(item.id)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          title="Remove upload"
        >
          <Icon name="trash" className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export const UploadItemComponent = React.memo(UploadItemComponentInner);
